import { useState, useEffect, useRef } from 'react';
import { Modal, Button, Typography, Spin, message } from 'antd';
import { CameraOutlined, ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';
import { getWalletOwnerInfo, promoteAttendeeToVendor } from '../services/vendorService';
import { colors } from '@festpix/ui';

const { Text, Title } = Typography;

type Step = 'scan' | 'confirm' | 'loading';

interface PromoteVendorModalProps {
  open: boolean;
  onClose: () => void;
  boothId: string;
  boothName: string;
}

interface WalletOwnerInfo {
  walletId: string;
  ownerId: string;
  ownerName: string;
  isChildWallet: boolean;
}

export default function PromoteVendorModal({ open, onClose, boothId, boothName }: PromoteVendorModalProps) {
  const [step, setStep] = useState<Step>('scan');
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<WalletOwnerInfo | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [scanned, setScanned] = useState(false);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      stopScanner();
      setStep('scan');
      setQrCodeData(null);
      setOwnerInfo(null);
      setIsScanning(false);
      setHasPermission(null);
      setScanned(false);
    }
  }, [open]);

  // Clean up scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch {
        // Ignore errors when stopping
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const startScanner = async () => {
    try {
      setIsScanning(true);
      setScanned(false);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('Camera requer conexao segura (HTTPS). Acesse via localhost ou HTTPS.');
        setHasPermission(false);
        setIsScanning(false);
        return;
      }

      // Request camera permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        stream.getTracks().forEach((track) => track.stop());
      } catch (err: any) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          message.error('Permissao de camera negada. Permita o acesso a camera.');
        } else if (err.name === 'NotFoundError') {
          message.error('Nenhuma camera encontrada no dispositivo.');
        } else {
          message.error('Erro ao acessar camera: ' + err.message);
        }
        setHasPermission(false);
        setIsScanning(false);
        return;
      }

      const html5QrCode = new Html5Qrcode('promote-qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          handleBarCodeScanned(decodedText);
        },
        () => {
          // No QR found in frame, ignore
        }
      );

      setHasPermission(true);
    } catch (err: any) {
      setHasPermission(false);
      setIsScanning(false);
      message.error('Erro ao iniciar a camera: ' + err.message);
    }
  };

  const handleBarCodeScanned = async (data: string) => {
    if (scanned) return;
    setScanned(true);
    await stopScanner();

    try {
      const parsed = JSON.parse(data);
      if (!parsed.walletId) {
        throw new Error('QR Code invalido');
      }

      const info = await getWalletOwnerInfo(data);

      if (info.isChildWallet) {
        message.error('Carteiras de dependentes nao podem ser promovidas a vendedor');
        setScanned(false);
        // Restart scanner for another try
        setTimeout(() => startScanner(), 500);
        return;
      }

      setQrCodeData(data);
      setOwnerInfo(info);
      setStep('confirm');
    } catch (error: any) {
      message.error(error.message || 'QR Code invalido');
      setScanned(false);
    }
  };

  const handleConfirmPromote = async () => {
    if (!qrCodeData) return;
    setStep('loading');

    try {
      await promoteAttendeeToVendor(qrCodeData, boothId);
      message.success(`${ownerInfo?.ownerName} foi promovido a vendedor com sucesso!`);
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Erro ao promover usuario');
      setStep('confirm');
    }
  };

  const handleBack = () => {
    setStep('scan');
    setQrCodeData(null);
    setOwnerInfo(null);
    setScanned(false);
    setTimeout(() => startScanner(), 300);
  };

  return (
    <Modal
      title="Promover a Vendedor"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={400}
    >
      {step === 'scan' && (
        <div>
          <Text style={{ color: colors.textSecondary, display: 'block', marginBottom: 16 }}>
            Escaneie o QR Code do visitante que deseja promover a vendedor
          </Text>

          <div
            style={{
              position: 'relative',
              width: '100%',
              minHeight: 280,
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 16,
            }}
          >
            {!isScanning && !scanned && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 10,
                }}
              >
                <CameraOutlined style={{ fontSize: 48, color: '#fff', marginBottom: 16 }} />
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={startScanner}
                  style={{
                    background: colors.success,
                    borderColor: colors.success,
                    borderRadius: 8,
                  }}
                >
                  Iniciar Camera
                </Button>
              </div>
            )}

            {isScanning && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                <Spin size="large" />
              </div>
            )}

            <div id="promote-qr-reader" style={{ width: '100%', minHeight: 280 }} />
          </div>

          {hasPermission === false && (
            <div
              style={{
                textAlign: 'center',
                padding: 16,
                background: `${colors.error}10`,
                borderRadius: 12,
                marginBottom: 16,
              }}
            >
              <Text style={{ color: colors.error, display: 'block', marginBottom: 12 }}>
                Sem acesso a camera. Permita o acesso nas configuracoes do navegador.
              </Text>
              <Button type="primary" icon={<ReloadOutlined />} onClick={startScanner}>
                Tentar Novamente
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'confirm' && ownerInfo && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              background: `${colors.success}20`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 16px',
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 32, color: colors.success }} />
          </div>

          <Title level={4} style={{ margin: '0 0 8px' }}>
            {ownerInfo.ownerName}
          </Title>
          <Text style={{ color: colors.textSecondary, display: 'block', marginBottom: 24 }}>
            Deseja promover esta pessoa para vendedor do estande <strong>{boothName}</strong>?
          </Text>

          <div
            style={{
              padding: 12,
              background: `${colors.warning}10`,
              borderRadius: 8,
              border: `1px solid ${colors.warning}40`,
              marginBottom: 24,
            }}
          >
            <Text style={{ color: colors.warning, fontSize: 13 }}>
              Esta acao dara acesso de vendedor a esta pessoa para este estande.
            </Text>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button size="large" style={{ flex: 1, borderRadius: 8 }} onClick={handleBack}>
              Voltar
            </Button>
            <Button
              type="primary"
              size="large"
              style={{
                flex: 1,
                borderRadius: 8,
                background: colors.success,
                borderColor: colors.success,
              }}
              onClick={handleConfirmPromote}
            >
              Confirmar
            </Button>
          </div>
        </div>
      )}

      {step === 'loading' && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, color: colors.textSecondary }}>
            Promovendo usuario...
          </Text>
        </div>
      )}
    </Modal>
  );
}
